import { supabase } from '@/lib/supabase';
import type { UserAccount, UserRole, UserStatus } from '@/types';

interface AdminResponse<T> {
  data: T | null;
  error: string | null;
}

const GENERIC_ERROR = 'Ocurrió un problema al procesar la solicitud. Intente nuevamente.';

async function invokeAdmin<T>(payload: Record<string, unknown>): Promise<AdminResponse<T>> {
  try {
    const { data, error } = await supabase.functions.invoke('admin-users', { body: payload });

    if (error) {
      let message = GENERIC_ERROR;
      const context = (error as { context?: Response }).context;
      if (context && typeof context.json === 'function') {
        try {
          const parsed = (await context.json()) as { error?: string };
          if (parsed?.error) message = parsed.error;
        } catch {
          // Se mantiene el mensaje genérico.
        }
      }
      return { data: null, error: message };
    }

    if (data && typeof data === 'object' && 'error' in data) {
      const message = (data as { error?: string }).error;
      if (message) return { data: null, error: message };
    }

    return { data: (data as T) ?? null, error: null };
  } catch {
    return { data: null, error: 'No se pudo conectar con el servidor. Intente nuevamente.' };
  }
}

export interface CreateUserInput {
  full_name: string;
  username: string;
  password: string;
  role: UserRole;
  status: UserStatus;
}

export interface UpdateUserInput {
  id: string;
  full_name: string;
  username: string;
  role: UserRole;
  status: UserStatus;
}

export function listUsers(): Promise<AdminResponse<{ users: UserAccount[] }>> {
  return invokeAdmin<{ users: UserAccount[] }>({ action: 'list' });
}

export function createUser(input: CreateUserInput): Promise<AdminResponse<unknown>> {
  return invokeAdmin({ action: 'create', ...input });
}

export function updateUser(input: UpdateUserInput): Promise<AdminResponse<unknown>> {
  return invokeAdmin({ action: 'update', ...input });
}

export function resetUserPassword(id: string, password: string): Promise<AdminResponse<unknown>> {
  return invokeAdmin({ action: 'reset-password', id, password });
}

export function deleteUser(id: string): Promise<AdminResponse<unknown>> {
  return invokeAdmin({ action: 'delete', id });
}